// Tax burden dimension — 100% stacked bar by decile, four Belgian
// fiscal collector tiers.
//
// One bar per decile (reform composition only). When the user toggles
// "Show baseline", each bar gets three small horizontal tick marks at
// the heights where the baseline segment boundaries sat. The reader
// sees the reform composition (filled colours) plus where each
// boundary used to be (dark ticks). Same convention as the per-bar
// baseline tick on the Household income chart, applied per segment.
//
// Why four categories (vs the three-category US/UK default): social
// security is a separate collector and a separate political-economy
// lever in Belgian debate.

import { useState } from "react";
import type { EChartsOption } from "echarts";
import { dimensionByKey } from "../../lib/dimensions";
import { formatPercent } from "../../lib/formatters";
import type { DecileRecord, EngineOutput } from "../../lib/types";
import { Chart } from "../chart";
import { HeadlineMetric } from "../headline_metric";

// Monochromatic purple ramp. Take-home gets the brand primary; tax
// categories use progressively lighter shades.
const COLOUR_TAKE_HOME = "#5e4a97";
const COLOUR_INCOME_TAX = "#8b7eae";
const COLOUR_SOCIAL_SECURITY = "#b1a8c5";
const COLOUR_CONSUMPTION_TAX = "#d6d0e0";

const BASELINE_TICK_COLOUR = "#3a3a3c";

// Deciles whose pre-tax income is ~0 or negative can't be drawn as a
// share-of-income composition; they get this muted, neutral-grey bar instead
// of a clipped one. (#205)
const COLOUR_NOT_APPLICABLE = "#d4d4d8";
const NOT_APPLICABLE_LABEL = "Not applicable";

interface Props {
  engineOutput: EngineOutput;
  baselineOutput: EngineOutput | null;
  isModified: boolean;
}

interface CategoryConfig {
  key: keyof Pick<
    DecileRecord,
    | "disposable_income_mean"
    | "income_tax_mean"
    | "social_security_mean"
    | "consumption_tax_mean"
  >;
  label: string;
  description: string;
  colour: string;
}

// Order bottom-to-top in the stack. Take-home anchored at the bottom
// (largest, "what you keep is your foundation"); taxes stack on top.
const CATEGORIES: readonly CategoryConfig[] = [
  {
    key: "disposable_income_mean",
    label: "Take-home",
    description: "Disposable income — what stays with the household after taxes.",
    colour: COLOUR_TAKE_HOME,
  },
  {
    key: "income_tax_mean",
    label: "Income tax",
    description: "Federal and regional personal income tax combined.",
    colour: COLOUR_INCOME_TAX,
  },
  {
    key: "social_security_mean",
    label: "Social security",
    description: "Employee social-security contributions.",
    colour: COLOUR_SOCIAL_SECURITY,
  },
  {
    key: "consumption_tax_mean",
    label: "Consumption taxes",
    description: "VAT and excise duties combined.",
    colour: COLOUR_CONSUMPTION_TAX,
  },
];

export function TaxBurdenStack({
  engineOutput,
  baselineOutput,
  isModified,
}: Props): JSX.Element {
  // Per-card UI state: the "Show baseline" toggle. Defaults on once
  // the user has modified anything — the change story is the primary
  // reason they're looking at the chart. The toggle lets them remove
  // the overlay for a cleaner composition read.
  const [showBaseline, setShowBaseline] = useState(true);

  const reformRecords = engineOutput.results.aggregator;
  const baselineRecords =
    baselineOutput?.results.aggregator ?? reformRecords;

  const taxBurdenBaseline =
    baselineOutput?.summary_stats.tax_burden ??
    engineOutput.summary_stats.tax_burden;
  const taxBurdenReform = engineOutput.summary_stats.tax_burden;

  const baselineOverlayActive = isModified && showBaseline;

  return (
    <div className="beamm-tax-burden-stack" data-testid="tax-burden-stack">
      <HeadlineMetric
        dimension={dimensionByKey.tax_burden}
        label="Average tax burden"
        baseline={taxBurdenBaseline}
        reform={taxBurdenReform}
        isModified={isModified}
      />
      {isModified && (
        <div className="beamm-chart-toolbar">
          <button
            type="button"
            className="beamm-chart-toolbar-toggle"
            aria-pressed={showBaseline}
            onClick={() => setShowBaseline((v) => !v)}
          >
            {showBaseline ? "Hide baseline" : "Show baseline"}
          </button>
        </div>
      )}
      <Chart
        option={buildOption(
          reformRecords,
          baselineRecords,
          baselineOverlayActive,
        )}
        height="360px"
      />
    </div>
  );
}

// The categories that actually carry data. A category whose value is zero in
// every decile (social security, which has no module yet) is dropped, so the
// chart never renders an empty band, an empty legend entry, or a coincident
// baseline tick. Data-driven, so the category re-appears automatically once a
// module populates it. Order is preserved, so take-home stays at the bottom.
export function activeCategories(
  records: DecileRecord[],
): readonly CategoryConfig[] {
  return CATEGORIES.filter((cat) => records.some((r) => r[cat.key] !== 0));
}

// A decile renders as a clean share-of-income composition only when its pre-tax
// income is positive and every category is non-negative — so each share lands in
// [0,1] and the four sum to 100%. Bottom deciles with ~0 or negative income, a
// negative take-home (post-consumption dissaving), or a refundable credit fail
// this; their shares would blow past the axis and clip into a wrong-but-plausible
// bar. Those deciles are drawn as a single "Not applicable" bar instead. (#205)
export function decileMeaningful(record: DecileRecord): boolean {
  return (
    record.pre_tax_income_mean > 0 &&
    CATEGORIES.every((cat) => record[cat.key] >= 0)
  );
}

export function buildOption(
  reformRecords: DecileRecord[],
  baselineRecords: DecileRecord[],
  showBaselineTicks: boolean,
): EChartsOption {
  const deciles = reformRecords.map((r) => String(r.decile));
  const cats = activeCategories(reformRecords);
  const meaningful = reformRecords.map(decileMeaningful);
  const anyNotApplicable = meaningful.some((m) => !m);

  const series: EChartsOption["series"] = [];

  // One stacked bar series per active category. Each segment value is
  // share-of-pre-tax-income; the active categories sum to 1 per decile by
  // construction (a dropped category contributed 0 anyway). Non-meaningful
  // deciles contribute 0 here and are filled by the "Not applicable" bar below.
  for (const cat of cats) {
    series.push({
      name: cat.label,
      type: "bar",
      stack: "reform",
      data: reformRecords.map((r, i) => (meaningful[i] ? shareFor(r, cat.key) : 0)),
      itemStyle: { color: cat.colour },
      barCategoryGap: "32%",
      z: 1,
    });
  }

  // A single full-height muted bar for deciles that can't be expressed as a
  // share-of-income composition (income ~0 or negative). Honest about the gap
  // rather than clipping a wrong bar. Only added when some decile needs it.
  if (anyNotApplicable) {
    series.push({
      name: NOT_APPLICABLE_LABEL,
      type: "bar",
      stack: "reform",
      data: meaningful.map((m) => (m ? 0 : 1)),
      itemStyle: { color: COLOUR_NOT_APPLICABLE },
      barCategoryGap: "32%",
      z: 1,
    });
  }

  // Baseline boundary ticks: one short horizontal mark per internal segment
  // boundary per bar (the topmost boundary is 100% by construction, so it
  // gets no tick). Boundaries follow the active categories, so dropping an
  // empty category also drops its coincident tick.
  if (showBaselineTicks) {
    const tickData: [number, number][] = [];
    const boundaries = computeBoundaries(baselineRecords, cats);
    boundaries.forEach((boundaryArray) => {
      boundaryArray.forEach((share, i) => {
        // No ticks on a "Not applicable" bar — its baseline boundaries are
        // just as ill-defined as the reform's.
        if (meaningful[i]) {
          tickData.push([i, share]);
        }
      });
    });
    series.push({
      name: "__baseline_ticks",
      type: "scatter",
      data: tickData,
      symbol: "rect",
      symbolSize: [38, 2.5],
      itemStyle: { color: BASELINE_TICK_COLOUR },
      silent: true,
      tooltip: { show: false },
      z: 5,
    });
  }

  return {
    grid: { left: "10%", right: "5%", top: "12%", bottom: "16%" },
    legend: {
      // Only the active category labels appear in the legend (plus the
      // "Not applicable" marker when present); the baseline-ticks scatter
      // series is filtered out by name.
      data: [
        ...cats.map((c) => c.label),
        ...(anyNotApplicable ? [NOT_APPLICABLE_LABEL] : []),
      ],
      bottom: 0,
      icon: "rect",
      itemWidth: 12,
      itemHeight: 12,
      textStyle: { fontSize: 12, color: "#3a3a3c" },
    },
    xAxis: {
      type: "category",
      name: "Decile",
      nameLocation: "middle",
      nameGap: 28,
      data: deciles,
    },
    yAxis: {
      type: "value",
      name: "Share of pre-tax income",
      max: 1,
      min: 0,
      axisLabel: {
        formatter: (v: number) => `${Math.round(v * 100)}%`,
      },
      splitLine: { lineStyle: { type: "dashed", color: "#ebe7f1" } },
    },
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      extraCssText: "max-width: 360px; white-space: normal;",
      formatter: (rawParams: unknown) => {
        const params = rawParams as Array<{ axisValue: string }>;
        const decile = params[0].axisValue;
        const idx = Number(decile) - 1;
        if (!meaningful[idx]) {
          return `<strong>Decile ${decile}</strong><br/>Income is around zero or negative here, so tax burden can't be shown as a share of income.`;
        }
        const r = reformRecords[idx];
        const b = baselineRecords[idx];
        const isModified = !recordsEqual(r, b);
        const lines: string[] = [`<strong>Decile ${decile}</strong>`];
        for (const cat of cats) {
          const reformShare = shareFor(r, cat.key);
          const baselineShare = shareFor(b, cat.key);
          const marker = `<span style="display:inline-block;width:8px;height:8px;background:${cat.colour};margin-right:6px;border-radius:2px;"></span>`;
          const valuePart = isModified
            ? `baseline ${formatPercent(baselineShare)} → reform ${formatPercent(reformShare)}`
            : `${formatPercent(reformShare)}`;
          lines.push(
            `${marker}<strong>${cat.label}:</strong> ${valuePart}`,
          );
        }
        const definitions = cats.map(
          (c) =>
            `<div style="font-size: 0.85em; color: #6e6e70; margin-left: 14px;">${c.label}: ${c.description}</div>`,
        ).join("");
        return lines.join("<br/>") + definitions;
      },
    },
    series,
  };
}

function shareFor(record: DecileRecord, key: CategoryConfig["key"]): number {
  if (record.pre_tax_income_mean === 0) return 0;
  return record[key] / record.pre_tax_income_mean;
}

// Cumulative-share boundaries for a stacked composition: one array per
// internal boundary (length = decile count), each tracing the upper edge of
// one category across deciles. The topmost boundary is always 100%, so the
// last category gets no array. Driven by the active categories, so a dropped
// empty category takes its boundary with it (no coincident ticks).
export function computeBoundaries(
  records: DecileRecord[],
  cats: readonly CategoryConfig[],
): number[][] {
  const boundaryCats = cats.slice(0, -1);
  const result: number[][] = boundaryCats.map(() => []);
  for (const r of records) {
    let cumulative = 0;
    boundaryCats.forEach((cat, i) => {
      cumulative += shareFor(r, cat.key);
      result[i].push(cumulative);
    });
  }
  return result;
}

function recordsEqual(a: DecileRecord, b: DecileRecord): boolean {
  return (
    a.disposable_income_mean === b.disposable_income_mean &&
    a.income_tax_mean === b.income_tax_mean &&
    a.social_security_mean === b.social_security_mean &&
    a.consumption_tax_mean === b.consumption_tax_mean
  );
}
